import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { User, Bike, Ruler, Leaf } from "lucide-react";

interface UserStatisticsCardProps {
  isLoading: boolean;
  data?: {
    mostActiveUser?: {
      name: string;
      totalRides: number;
      totalDistance: number;
    };
    averagePerUser?: {
      rides: number;
      totalDistance: number;
      offsetDistance: number;
      nonOffsetDistance: number;
      co2Offset: number;
    };
  };
}

const UserStatisticsCard: React.FC<UserStatisticsCardProps> = ({ isLoading, data }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>User Statistics</CardTitle>
        <CardDescription>User activity and averages</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Most Active User Section */}
            <div className="rounded-lg border p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" />
                <span>Most Active User</span>
              </div>
              <div className="mt-2">
                <p className="text-xl font-bold">{data?.mostActiveUser?.name || 'No data'}</p>
                <div className="mt-1 flex items-center gap-4 text-sm text-muted-foreground">
                  <span>{data?.mostActiveUser?.totalRides || 0} rides</span>
                  <span>{data?.mostActiveUser?.totalDistance?.toFixed(1) || 0} km</span>
                </div>
              </div>
            </div>

            {/* Average Stats Per User */}
            <div className="grid gap-4">
              <div className="rounded-lg border p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Bike className="h-4 w-4" />
                  <span>Average Per User</span>
                </div>
                <div className="mt-2 space-y-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Average Rides</p>
                    <p className="text-lg font-semibold">{data?.averagePerUser?.rides?.toFixed(1) || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Average Distance</p>
                    <p className="text-lg font-semibold">{data?.averagePerUser?.totalDistance?.toFixed(1) || 0} km</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Ruler className="h-4 w-4" />
                  <span>Average Distance Breakdown</span>
                </div>
                <div className="mt-2 space-y-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Average Offset Distance</p>
                    <p className="text-lg font-semibold">{data?.averagePerUser?.offsetDistance?.toFixed(1) || 0} km</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Average Non-Offset Distance</p>
                    <p className="text-lg font-semibold">{data?.averagePerUser?.nonOffsetDistance?.toFixed(1) || 0} km</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border bg-green-50 p-4">
                <div className="flex items-center gap-2 text-sm text-green-800">
                  <Leaf className="h-4 w-4" />
                  <span>Average CO2 Offset</span>
                </div>
                <div className="mt-2">
                  <p className="text-2xl font-bold text-green-800">
                    {data?.averagePerUser?.co2Offset?.toFixed(2) || 0} kg
                  </p>
                  <p className="mt-1 text-sm text-green-600">
                    Based on UK DEFRA conversion factors
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserStatisticsCard;
